import React, { useCallback, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import { PAYMENT_TYPES } from "../../witadmin/Common/constants";
import { TransactionDto } from "../index";
import { AmountRender, DateRender } from "../renders";
import { StyledSpinner } from "../styles";
import { OperationTypeEnum } from "./model";
import { StyledIcon, StyledTable } from "./styles";

const createMessage = <T extends Record<string, Record<string, MessageDescriptor>>>(messages: T) =>
  messages;

const messages = createMessage({
  operationType: {
    refund: {
      id: "reservationDetail.transactionList.operationType.refund",
      defaultMessage: "Refund",
    },
    charge: {
      id: "reservationDetail.transactionList.operationType.charge",
      defaultMessage: "Charge",
    },
    authorization: {
      id: "reservationDetail.transactionList.operationType.authorization",
      defaultMessage: "Authorization",
    },
    deposit: {
      id: "reservationDetail.transactionList.operationType.deposit",
      defaultMessage: "Deposit",
    },
  },
  transactionList: {
    title: {
      id: "reservationDetail.transactionList.title",
      defaultMessage: "Transaction List",
    },
    noRecordsFound: {
      id: "reservationDetail.transactionList.noRecordsFound",
      defaultMessage: "No records found",
    },
    bookingEngineCharge: {
      id: "reservationDetail.transactionList.bookingEngineCharge",
      defaultMessage: "Default charge",
    },
    bookingEngineRefund: {
      id: "reservationDetail.transactionList.bookingEngineRefund",
      defaultMessage: "Backoffice refund",
    },
    tableHeader: {
      date: {
        id: "reservationDetail.transactionList.tableHeader.date",
        defaultMessage: "Date",
      },
      transactionId: {
        id: "reservationDetail.transactionList.tableHeader.transactionId",
        defaultMessage: "Transaction ID",
      },
      orderNumber: {
        id: "reservationDetail.transactionList.tableHeader.orderNumber",
        defaultMessage: "Order number",
      },
      total: {
        id: "reservationDetail.transactionList.tableHeader.total",
        defaultMessage: "Total (EUR)",
      },
      type: {
        id: "reservationDetail.transactionList.tableHeader.type",
        defaultMessage: "Type",
      },
      description: {
        id: "reservationDetail.transactionList.tableHeader.description",
        defaultMessage: "Description",
      },
      status: {
        id: "reservationDetail.transactionList.tableHeader.status",
        defaultMessage: "Status",
      },
      creditCardNumber: {
        id: "reservationDetail.transactionList.tableHeader.creditCardNumber",
        defaultMessage: "Card",
      },
      user: {
        id: "reservationDetail.transactionList.tableHeader.user",
        defaultMessage: "User",
      },
      totalSum: {
        id: "reservationDetail.transactionList.tableHeader.totalSum",
        defaultMessage: "Total Sum",
      },
    },
  },
  errors: {
    getTransactionsError: {
      id: "reservationDetail.transactionList.getTransactionsError",
      defaultMessage: "Error on getting transactions",
    },
  },
});

export interface TransactionListProps {
  isLoading: boolean;
  data: TransactionDto[];
  paymentType?: string;
}

function isSummarizeTransaction(transaction: TransactionDto, paymentType?: string): boolean {
  if (paymentType === PAYMENT_TYPES.NEMURU) {
    return transaction.type === "capture_approved";
  }

  return !["KO", "REFUSED"].includes(transaction.status);
}

export function TransactionList(props: TransactionListProps) {
  const intl = useIntl();

  const transactions = useMemo(() => {
    return props.data.map(it => ({
      ...it,
      total: it.type === "REFUND" ? it.total * -1 : it.total,
    }));
  }, [props.data]);

  const summarizeTotal = useMemo(() => {
    return transactions
      .filter((it: TransactionDto) => isSummarizeTransaction(it, props.paymentType))
      .map((it: TransactionDto) => it.total)
      .reduce((acc: number, curr: number) => acc + curr, 0);
  }, [props.paymentType, transactions]);

  const getSafeDescription = useCallback(
    (description?: string) => {
      switch (description) {
        case "BOOKING_ENGINE_CHARGE":
          return <FormattedMessage {...messages.transactionList.bookingEngineCharge} />;
        case "BOOKING_ENGINE_REFUND":
          return <FormattedMessage {...messages.transactionList.bookingEngineRefund} />;
        case "DEBIT":
        case "AUTHORIZATION":
        case "AUTHORISATION":
          return intl.formatMessage(messages.operationType.authorization);
        case "DEPOSIT":
          return intl.formatMessage(messages.operationType.deposit);
        default:
          return description;
      }
    },
    [intl]
  );

  const safeOperationTypeTranslation = useCallback(
    (type?: string) => {
      switch (type?.toUpperCase()) {
        case OperationTypeEnum.DEPOSIT:
        case OperationTypeEnum.DEBIT:
        case OperationTypeEnum.AUTHORIZATION:
        case OperationTypeEnum.AUTHORISATION:
          return intl.formatMessage(messages.operationType.authorization);
        case OperationTypeEnum.CHARGE:
          return intl.formatMessage(messages.operationType.charge);
        case OperationTypeEnum.REFUND:
          return intl.formatMessage(messages.operationType.refund);
        default:
          return type;
      }
    },
    [intl]
  );

  return (
    <div>
      <div className="border-bottom pb-2 mb-3 text-[21px]">
        <FormattedMessage {...messages.transactionList.title} />
        {props.isLoading && (
          <span>
            {" "}
            <StyledSpinner className="fa fa-spinner fa-spin" />
          </span>
        )}
      </div>
      <div className="mb-3">
        <StyledTable className="table table-striped table-borderless">
          <thead>
            <tr>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.date} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.transactionId} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.orderNumber} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.total} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.type} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.description} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.status} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.creditCardNumber} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.user} />
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions && transactions.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <FormattedMessage {...messages.transactionList.noRecordsFound} />
                </td>
              </tr>
            ) : (
              transactions.map((it: TransactionDto, index) => (
                <tr key={index}>
                  <td>
                    <DateRender date={it.date} />
                  </td>
                  <td>{it.id}</td>
                  <td>{it.orderNumber}</td>
                  <td>
                    <AmountRender amount={it.total} />
                  </td>
                  <td>{safeOperationTypeTranslation(it.type)}</td>
                  <td>{getSafeDescription(it.description)}</td>
                  <td>
                    <span>{it.status}</span>
                    {it.statusInfo && (
                      <span className="ml-2">
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip id="error-tooltip" {...props}>
                              {it.statusInfo}
                            </Tooltip>
                          }>
                          <StyledIcon className="fa fa-info-circle" />
                        </OverlayTrigger>
                      </span>
                    )}
                  </td>
                  <td>{it.creditCardNumber}</td>
                  <td>{it.user}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.totalSum} />
              </th>
              <th colSpan={2}></th>
              <th>
                <AmountRender amount={summarizeTotal} />
              </th>
              <th colSpan={5} />
            </tr>
          </tfoot>
        </StyledTable>
      </div>
    </div>
  );
}
